import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { HttpParams } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { GruppeTier } from '../../../../../projects/kiene-core/src/lib/components/gruppe-tier/classes/gruppe-tier';
import { API_BASE_URL_SERVICE } from '../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { KieneBackendApiService } from '../../../../../projects/kiene-core/src/lib/services/backend-api/kiene-backend-api.service';
import { LocalStorageService } from '../../../../../projects/kiene-core/src/lib/services/local-storage/local-storage.service';
import { MessageService } from '../../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { LETZTE_AKTIONEN_ZEITRAUM_DEFAULT, LetzteAktionenZeitraum } from '../letzte-aktionen-zeitraum';

export interface PaesseAbgleichenTier extends GruppeTier {
    paesse_tierliste_id?: number | null;
    paesse_tierliste_status_id?: number | null;
    paesse_has_letzte_aktion?: number;
    paesse_in_zeitraum?: number;
    paesse_navigable?: number;
    paesse_rank?: number;
    paesse_hint?: string | null;
}

interface PaesseAbgleichenMatchResponse {
    records?: PaesseAbgleichenTier[];
    recommended_tierliste_id?: number | null;
    navigation_hint?: string | null;
}

type PaesseAbgleichenSearch =
    | { kind: 'ohrmarke15'; value: string }
    | { kind: 'ohrmarke'; value: string }
    | { kind: 'ohrmarke_kurz'; value: string };

@Component({
    selector: 'kiene-paesse-abgleichen',
    templateUrl: './paesse-abgleichen.component.html',
    styleUrls: ['./paesse-abgleichen.component.scss'],
})
export class PaesseAbgleichenComponent implements OnInit, OnDestroy {
    @Output() tierlisteIdFound = new EventEmitter<number | null>();
    @Input() showToggleButton = true;
    @Input() letzteAktionenZeitraum: LetzteAktionenZeitraum = LETZTE_AKTIONEN_ZEITRAUM_DEFAULT;

    showSearchField = false;
    searchCtrl = new FormControl('');
    isLoading = false;
    searchResults: PaesseAbgleichenTier[] = [];
    selectedTier: PaesseAbgleichenTier | null = null;
    showTierlisteContainer = false;
    lastScannedValue = '';
    navigationHint: string | null = null;

    private searchSubscription: Subscription;
    private clearedByEnter = false;
    private clearedAfterSearch = false;

    constructor(
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
        private readonly api: KieneBackendApiService,
        private readonly localStorageService: LocalStorageService,
        private readonly messageService: MessageService,
    ) {}

    ngOnInit(): void {
        this.initSearch();
    }

    ngOnDestroy(): void {
        this.searchSubscription?.unsubscribe();
    }

    toggleSearchField(): void {
        this.showSearchField = !this.showSearchField;
        if (!this.showSearchField) {
            this.searchCtrl.setValue('');
            this.searchResults = [];
            this.selectedTier = null;
            this.showTierlisteContainer = false;
            this.navigationHint = null;
            this.emitTierlisteId(null);
        }
    }

    selectTier(tier: PaesseAbgleichenTier): void {
        this.selectedTier = tier;
        const selectedTierlisteId = this.resolveTierlisteId(tier);
        const navigable = Number(tier?.paesse_navigable || 0) > 0;
        if (!navigable) {
            const hint = tier?.paesse_hint || 'Keine passende Aktion in Letzte Aktionen gefunden.';
            this.navigationHint = hint;
            this.messageService.infoMessage(hint);
            this.emitTierlisteId(null);
            return;
        }
        this.navigationHint = null;
        this.emitTierlisteId(selectedTierlisteId > 0 ? selectedTierlisteId : null);
    }

    getTierlisteStatusLabel(tier: PaesseAbgleichenTier): string {
        const statusId = Number(tier?.paesse_tierliste_status_id || 0);
        if (statusId === 163) {
            return 'Abgeschlossen';
        }
        if (statusId === 162) {
            return 'Ausgestallt';
        }
        if (statusId === 161) {
            return 'Erstellt';
        }
        return '';
    }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            event.preventDefault();
            return;
        }

        const currentValue = this.sanitizeSearchInput((event.target as HTMLInputElement)?.value || '');
        const maxLen = this.getMaxSearchLength(currentValue);
        if (
            currentValue.length >= maxLen &&
            event.key !== 'Backspace' &&
            event.key !== 'Delete' &&
            !event.ctrlKey &&
            !event.metaKey
        ) {
            event.preventDefault();
        }
    }

    onPaste(event: ClipboardEvent): void {
        event.preventDefault();
        const clipboardData = event.clipboardData || (window as unknown as { clipboardData?: DataTransfer }).clipboardData;
        const pastedText = clipboardData ? clipboardData.getData('text') : '';
        const trimmedValue = this.trimSearchInput(pastedText);
        (event.target as HTMLInputElement).value = trimmedValue;
        this.searchCtrl.setValue(trimmedValue, { emitEvent: true });
    }

    onInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        const trimmedValue = this.trimSearchInput(input.value);
        if (input.value !== trimmedValue) {
            input.value = trimmedValue;
            this.searchCtrl.setValue(trimmedValue, { emitEvent: true });
        }
    }

    private initSearch(): void {
        this.searchSubscription = this.searchCtrl.valueChanges
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                filter(value => !value || value.length <= 15),
            )
            .subscribe(value => {
                if (value && value.trim() !== '' && !this.clearedByEnter && !this.clearedAfterSearch) {
                    this.showTierlisteContainer = false;
                    this.clearedAfterSearch = false;
                    this.lastScannedValue = '';
                    this.navigationHint = null;
                    this.emitTierlisteId(null);
                }

                if (value && this.parseSearchValue(value)) {
                    this.clearedByEnter = false;
                    this.clearedAfterSearch = false;
                    this.searchTiere();
                } else if (!value && !this.clearedByEnter && !this.clearedAfterSearch) {
                    this.searchResults = [];
                    this.selectedTier = null;
                    this.navigationHint = null;
                }
            });
    }

    searchTiere(): void {
        const search = this.parseSearchValue(this.searchCtrl.value);
        if (!search) {
            return;
        }

        this.lastScannedValue = search.value;
        this.isLoading = true;
        this.searchResults = [];
        this.selectedTier = null;
        this.navigationHint = null;

        let params = new HttpParams().set('zeitraum', this.letzteAktionenZeitraum);
        if (search.kind === 'ohrmarke15') {
            params = params.set('ohrmarke15', search.value);
        } else if (search.kind === 'ohrmarke_kurz') {
            params = params.set('ohrmarke_kurz', search.value);
        } else {
            params = params.set('ohrmarke', search.value);
        }
        const currentClient = this.localStorageService.getCurrentClient();
        if (currentClient?.kunde_id) {
            params = params.set('kunde_id', currentClient.kunde_id.toString());
        }

        this.api.get(this.apiBaseUrl + 'gruppe/read_paesse_abgleichen_match.php', params).subscribe({
            next: (response: PaesseAbgleichenMatchResponse) => {
                this.handleSearchSuccess(response);
            },
            error: error => {
                this.isLoading = false;
                this.messageService.errorMessage('Fehler bei der Suche', error);
            },
        });
    }

    private handleSearchSuccess(response: PaesseAbgleichenMatchResponse): void {
        this.isLoading = false;
        const records = (response?.records ?? []) as PaesseAbgleichenTier[];
        this.searchResults = records;
        this.showTierlisteContainer = records.length > 0;
        this.clearedAfterSearch = true;
        this.searchCtrl.setValue('');

        if (records.length === 0) {
            this.emitTierlisteId(null);
            this.messageService.infoMessage('Keine Tiere mit dieser Ohrmarke gefunden.');
            return;
        }

        const navigable = records.filter(tier => Number(tier.paesse_navigable || 0) > 0);
        const autoPick = navigable.length === 1 ? navigable[0] : null;
        if (!autoPick && records.length === 1) {
            this.selectTier(records[0]);
            return;
        }
        if (autoPick) {
            this.selectTier(autoPick);
            return;
        }

        const hint = response?.navigation_hint?.trim() || null;
        this.navigationHint = hint;
        if (hint) {
            this.messageService.infoMessage(hint);
        }
    }

    private sanitizeSearchInput(value: string): string {
        return value.trim().toUpperCase().replace(/\s/g, '');
    }

    private trimSearchInput(value: string): string {
        const sanitized = this.sanitizeSearchInput(value);
        const countryMatch = /^[A-Z]{2}/.test(sanitized);
        if (countryMatch) {
            return sanitized.substring(0, 12);
        }
        return this.normalizeDigitInput(sanitized).substring(0, 15);
    }

    /** Strip check digits from 12-digit ear-tag scans; keep 15-digit pass scans as-is. */
    private normalizeDigitInput(digitsOnly: string): string {
        if (digitsOnly.length === 12 && digitsOnly.startsWith('27600')) {
            return digitsOnly;
        }
        if (digitsOnly.length === 12) {
            return digitsOnly.slice(0, 10);
        }
        return digitsOnly;
    }

    private getMaxSearchLength(value: string): number {
        return /^[A-Z]{2}/.test(value) ? 12 : 15;
    }

    private parseSearchValue(value: string | null | undefined): PaesseAbgleichenSearch | null {
        if (!value) {
            return null;
        }
        const searchValue = this.sanitizeSearchInput(value);
        const countryMatch = /^([A-Z]{2})(\d{10})$/.exec(searchValue);
        if (countryMatch) {
            return { kind: 'ohrmarke', value: countryMatch[1] + countryMatch[2] };
        }

        if (searchValue.length === 15 && /^\d{15}$/.test(searchValue)) {
            return { kind: 'ohrmarke15', value: searchValue };
        }
        if (searchValue.length === 12 && /^\d{12}$/.test(searchValue)) {
            const normalized = this.normalizeDigitInput(searchValue);
            if (normalized.length === 10 && /^\d{10}$/.test(normalized)) {
                return { kind: 'ohrmarke_kurz', value: normalized };
            }
        }
        if (searchValue.length === 10 && /^\d{10}$/.test(searchValue)) {
            return { kind: 'ohrmarke_kurz', value: searchValue };
        }
        return null;
    }

    resolveTierlisteId(tier: PaesseAbgleichenTier | null | undefined): number {
        return Number(tier?.paesse_tierliste_id || tier?.tierliste_id || tier?.einstall_tierliste_id || 0);
    }

    private emitTierlisteId(tierlisteId: number | null): void {
        this.tierlisteIdFound.emit(tierlisteId && tierlisteId > 0 ? tierlisteId : null);
    }
}
